import { ComponentProps, useMemo } from "react";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { formatMessage } from "../../../../utils";
import { FlightDetails } from "../../Flight/FlightDetails";
import { FlightRefundInfos } from "../../Flight/FlightRefundInfos";
import { FlightSummaryBaseComponentProps } from "../../Flight/FlightSummary";
import { FlightPrebookRefundInfos } from "../FlightPrebookRefundInfos";
import { FlightSummaryDetailItem, RefundInfoApiData } from "./type";

export function useFlightSummaryDetails(
  showByIndex: number | null,
  summaries: ReadonlyArray<FlightSummaryBaseComponentProps>,
  refundInfo: RefundInfoApiData
): FlightSummaryDetailItem {
  const { CorporateFlightProductSummary: cr } = useMobileContentResource();

  const transformedRefundInfo = useMemo(() => {
    return {
      data:
        refundInfo.data?.map((item) =>
          item.refundPolicies.map((p) => ({
            status: p.refundPolicy.length ? "REFUNDABLE" : "NON_REFUNDABLE",
            policies: p.refundPolicy,
          }))
        ) || null,
      isLoading: refundInfo.isLoading,
      onRetry: refundInfo.onRetry,
    };
  }, [refundInfo]);

  return useMemo(() => {
    if (showByIndex === null) return { title: "", subtitle: "", items: [] };

    const { summary, trip } = summaries[showByIndex];
    const from = summary.departureDetail.airport.airportCode;
    const to = summary.arrivalDetail.airport.airportCode;
    const subtitle = `${from} - ${to} • ${summary.duration} • ${formatMessage(
      cr.numOfTransit,
      { num: summary.numOfTransit }
    )}`;

    const refundInfos:
      | ComponentProps<typeof FlightRefundInfos>["refundInfos"]
      | null = transformedRefundInfo.data
      ? transformedRefundInfo.data[showByIndex].map((item, idx) => {
          const journey = trip.journeys[idx];
          return {
            airlineName: journey.airlineNames,
            airlineLogos: journey.airlineLogos,
            seatClass: journey.seatClass,
            departureCity: journey.departureAirportCity,
            arrivalCity: journey.arrivalAirportCity,
            status: item.status as "REFUNDABLE" | "NON_REFUNDABLE",
            policies: item.policies,
          };
        })
      : null;

    return {
      title: summary.airlineName,
      subtitle,
      items: [
        {
          label: "Flight details",
          key: `flight-summary-${showByIndex}`,
          content: <FlightDetails {...trip} />,
        },
        {
          label: "Refund policy",
          key: `refund-policy-${showByIndex}`,
          content: (
            <FlightPrebookRefundInfos
              refundInfos={refundInfos}
              isLoading={transformedRefundInfo.isLoading}
              onRetry={transformedRefundInfo.onRetry}
            />
          ),
        },
      ],
    };
  }, [showByIndex, summaries, transformedRefundInfo, cr]);
}
